import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { openReportDialog } from '@/stores/reportDialogStore';

import { HaiIcon } from '@/components/icons';

/**
 * 全域 Toast Provider
 * -----------------------------------------------------------------------------
 * 為什麼用 Context 而不是 zustand：toast 是 ephemeral UI 狀態，沒有跨 tab 持久化、
 * 不需 selector 優化、不該被 devtools 追蹤。Context 也天然支援未來 modal 內獨立
 * toast queue（多個 Provider 嵌套）。
 *
 * 用法：
 *   const { show } = useToast();
 *   show({ message: '已執行', tone: 'success' });
 *
 * 自動 dismiss（預設 3 秒），呼叫端不必管 timer。
 */

export type ToastTone = 'info' | 'success' | 'error';

/** R209 — toast 內嵌動作(批次刪除 undo /「復原」慣例;D3 素材庫.html #toast button)。 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastInput {
  message: string;
  tone?: ToastTone;
  /** 顯示時間（ms），預設 3000;帶 `action` 時預設 6000(給使用者反應時間)。 */
  durationMs?: number;
  /** 內嵌動作鈕(如「復原」);點擊後 toast 自動關閉。 */
  action?: ToastAction;
  /**
   * R249 — 設 `false` 可關掉自動補上的「回報」動作鈕。
   *
   * 預設(未指定)對 `tone: 'error'` 的 toast **自動補**一顆回報鈕:錯誤發生的那一刻是
   * 回報成本最低、診斷最完整的時機(緩衝裡剛好含那筆失敗請求),而逐處去標記「這個
   * 錯誤可以回報」永遠標不完 —— 漏掉一個系統錯誤的回報出口,比多一顆鈕貴。
   *
   * 明確屬於**使用者操作限制**而非系統故障的提示(檔案太大 / 素材已滿 / 字數超過),
   * 傳 `reportable: false` 關掉,避免變成噪音。
   */
  reportable?: boolean;
}

interface ToastEntry extends Required<Pick<ToastInput, 'message'>> {
  id: string;
  tone: ToastTone;
  durationMs: number;
  action?: ToastAction;
}

interface ToastContextValue {
  show(input: ToastInput): void;
  dismiss(id: string): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 3000;
/** 帶動作鈕(undo)的 toast 停留時間 — 對齊 D3 library.js toast undo 6s。 */
const ACTION_DURATION = 6000;

let nextToastSerial = 1;

export function ToastProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation('common');
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const timers = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const show = useCallback(
    (input: ToastInput) => {
      const id = `toast-${Date.now()}-${nextToastSerial++}`;
      // R249 — 錯誤 toast 自動掛「回報」。這是**全站單點**:幾十處 `showToast({tone:'error'})`
      // 一個都不用改,錯誤發生當下就有回報出口(而且緩衝裡剛好含那筆失敗請求)。
      // 呼叫端自帶 action 時不覆蓋(那顆通常是「重試」,比回報更該優先);
      // `reportable: false` 可 opt-out(見 ToastInput 該欄註解)。
      const action: ToastAction | undefined =
        input.action ??
        ((input.tone ?? 'info') === 'error' && input.reportable !== false
          ? { label: t('report.toastAction'), onClick: () => openReportDialog() }
          : undefined);
      const entry: ToastEntry = {
        id,
        message: input.message,
        tone: input.tone ?? 'info',
        // 帶 action(含自動補的那顆)一律用較長的顯示時間 —— 3 秒不夠讓人讀完訊息
        // 又決定要不要按。
        durationMs: input.durationMs ?? (action ? ACTION_DURATION : DEFAULT_DURATION),
        ...(action ? { action } : {}),
      };
      setEntries((prev) => [...prev, entry]);
      const handle = window.setTimeout(() => dismiss(id), entry.durationMs);
      timers.current.set(id, handle);
    },
    [dismiss, t],
  );

  // Unmount 清掉殘留 timer，避免設定在已卸載 component 上的 setState
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const handle of map.values()) window.clearTimeout(handle);
      map.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer entries={entries} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider>');
  }
  return ctx;
}

// ─── 視覺層 ───────────────────────────────────────────────────────────────────

// R48 Tier 6 — 改用 semantic token color-mix 統一視覺。
// R393 — CIS 語意色:成功 = teal(CIS 沒有綠色)、錯誤 = rose(R266 `--ok` / `--err` 只當淡底)
const TONE_BORDER: Record<ToastTone, string> = {
  info: 'var(--hairline-strong)',
  success: 'color-mix(in oklab, var(--tide-600) 40%, transparent)',
  error: 'color-mix(in oklab, var(--rose-600) 50%, transparent)',
};

function ToastContainer({
  entries,
  onDismiss,
}: {
  entries: ToastEntry[];
  onDismiss: (id: string) => void;
}) {
  if (entries.length === 0) return null;
  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    >
      {entries.map((entry) => (
        <ToastItem key={entry.id} entry={entry} onDismiss={() => onDismiss(entry.id)} />
      ))}
    </div>
  );
}

function ToastItem({
  entry,
  onDismiss,
}: {
  entry: ToastEntry;
  onDismiss: () => void;
}): ReactNode {
  return (
    <div
      role="status"
      style={{
        position: 'relative',
        padding: '12px 36px 12px 18px',
        background: 'var(--ink-850)',
        border: `1px solid ${TONE_BORDER[entry.tone]}`,
        borderRadius: 'var(--r-sm)',
        color: 'var(--ink-100)',
        fontSize: 'var(--text-sm)',
        boxShadow: 'var(--shadow-md)',
        animation: 'slideUp 0.3s ease-out',
        pointerEvents: 'auto',
        maxWidth: 360,
      }}
    >
      {entry.message}
      {entry.action && (
        <button
          type="button"
          onClick={() => {
            entry.action?.onClick();
            onDismiss();
          }}
          style={{
            marginLeft: 12,
            padding: '2px 4px',
            border: 'none',
            background: 'none',
            borderRadius: 6,
            // teal 文字走 --concept-fg(淺色 toast 是淺底,--tide-400 只有 ~1.8:1)
            color: 'var(--concept-fg)',
            fontSize: 'var(--text-sm)',
            fontWeight: 700,
            cursor: 'pointer',
          }}
          data-testid="toast-action"
        >
          {entry.action.label}
        </button>
      )}
      <button
        type="button"
        aria-label="dismiss"
        onClick={onDismiss}
        style={{
          position: 'absolute',
          top: 8,
          right: 10,
          width: 20,
          height: 20,
          display: 'grid',
          placeItems: 'center',
          color: 'var(--ink-500)',
          lineHeight: 1,
          cursor: 'pointer',
          border: 'none',
          background: 'none',
        }}
      >
        {/* R209 — unicode × 換 icon 單一來源(CIS 禁 unicode 字元圖示) */}
        <HaiIcon name="x" size={11} />
      </button>
    </div>
  );
}
